import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { HealthBar } from '../../HealthBar.js';
import { PALETTE, matte, colored } from '../../Style.js';

const STEEL = 0xaab0b8;
const STEEL_DARK = 0x6c7079;
const GOLD = 0xd6a640;
const VISOR = 0x1d1c22;
const LEATHER = PALETTE.woodDark;

// The crossbow arm reaches across to hold the weapon low in front of the body.
// Everything stays inside the server's 1.0 collision radius so nothing pokes into walls.
const AIM_POSE_X = -0.35;
const AIM_POSE_Z = -0.45;
const CAPE_REST = 0.08;

// Widen the bottom of a geometry so cloth flares out as it hangs
function flare(geometry, amount) {
  const pos = geometry.attributes.position;
  geometry.computeBoundingBox();
  const { min, max } = geometry.boundingBox;
  for (let i = 0; i < pos.count; i++) {
    const t = (max.y - pos.getY(i)) / (max.y - min.y);
    pos.setX(i, pos.getX(i) * (1 + amount * t));
  }
  return geometry;
}

// The hero: a chunky knight with a team-coloured tabard, cape and shield, carrying a crossbow
export class Player {
  constructor(scene, unit, color, isLocalPlayer = false, displayName = null) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.isLocalPlayer = isLocalPlayer;
    this.displayName = displayName || `Player ${unit.ownerId + 1}`;
    this.mesh = null;
    this.lastPosition = null;
    this.targetRotation = 0;
    this.respawnOverlay = null;
    this.nametagSprite = null;
    this.healthBar = null;
    this.maxHealth = 10; // PlayerUnitHealth from server
    this.isRespawning = null;
    this.geometries = [];
    this.materials = [];
    this.walk = 0;
    this.walkPhase = 0;
    this.lastAnimTime = null;
    this.lastMeshPosition = new THREE.Vector3();
    this.create();
  }

  // Merge coloured parts into a single mesh using this knight's material
  part(parts) {
    const geometry = mergeGeometries(parts);
    parts.forEach(p => p.dispose());
    this.geometries.push(geometry);
    const mesh = new THREE.Mesh(geometry, this.material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  // A pivoting limb attached to the body
  limb(parts, x, y, z) {
    const pivot = new THREE.Group();
    pivot.position.set(x, y, z);
    pivot.add(this.part(parts));
    this.body.add(pivot);
    return pivot;
  }

  create() {
    const bodyGroup = new THREE.Group();

    // Per-instance material so respawn dimming only affects this knight
    this.material = matte(0xffffff, { vertexColors: true, unique: true, roughness: 0.8 });
    this.material.userData.baseOpacity = 1;
    this.materials.push(this.material);

    const team = new THREE.Color(this.color);
    const teamDark = team.clone().multiplyScalar(0.65);

    // Everything that bobs while walking hangs off the body
    this.body = new THREE.Group();
    bodyGroup.add(this.body);

    // Torso: mail shirt under a team tabard, belt, gold emblem
    this.body.add(this.part([
      colored(new THREE.CylinderGeometry(0.48, 0.56, 0.4, 8), STEEL_DARK, { y: 0.98 }),
      colored(new THREE.BoxGeometry(1.0, 1.1, 0.68), STEEL_DARK, { y: 1.76 }),
      colored(new THREE.BoxGeometry(0.84, 1.4, 0.76), team, { y: 1.62 }),
      colored(new THREE.BoxGeometry(0.9, 0.14, 0.82), LEATHER, { y: 1.3 }),
      colored(new THREE.BoxGeometry(0.2, 0.16, 0.06), GOLD, { y: 1.3, z: 0.42 }),
      colored(new THREE.BoxGeometry(0.28, 0.28, 0.04), GOLD, { y: 1.88, z: 0.39, rz: Math.PI / 4 }),
      // Rounded team pauldrons, readable from above
      colored(new THREE.SphereGeometry(0.32, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), team, { x: -0.58, y: 2.2, scale: [1.05, 0.9, 1.05] }),
      colored(new THREE.SphereGeometry(0.32, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), team, { x: 0.58, y: 2.2, scale: [1.05, 0.9, 1.05] }),
      colored(new THREE.CylinderGeometry(0.32, 0.38, 0.18, 8), STEEL_DARK, { y: 2.4 }),
      // Oversized great helm with a T-shaped visor and a team crest
      colored(new THREE.CylinderGeometry(0.5, 0.52, 0.62, 8), STEEL, { y: 2.8 }),
      colored(new THREE.SphereGeometry(0.5, 8, 3, 0, Math.PI * 2, 0, Math.PI / 2), STEEL, { y: 3.11, scale: [1, 0.72, 1] }),
      colored(new THREE.CylinderGeometry(0.53, 0.53, 0.08, 8), GOLD, { y: 3.08 }),
      colored(new THREE.BoxGeometry(0.62, 0.09, 0.06), VISOR, { y: 2.88, z: 0.49 }),
      colored(new THREE.BoxGeometry(0.09, 0.34, 0.06), VISOR, { y: 2.72, z: 0.49 }),
      colored(new THREE.BoxGeometry(0.14, 0.24, 0.66), team, { y: 3.48, z: -0.04 }),
      colored(new THREE.BoxGeometry(0.14, 0.2, 0.32), teamDark, { y: 3.34, z: -0.44, rx: 0.5 }),
      // Crossbow held low across the body, nose dipped, pulled in close
      colored(new THREE.BoxGeometry(0.12, 0.14, 0.8), PALETTE.wood, { x: 0.2, y: 1.45, z: 0.32, rx: 0.2 }),
      colored(new THREE.BoxGeometry(0.14, 0.22, 0.24), PALETTE.woodDark, { x: 0.2, y: 1.52, z: -0.02, rx: 0.2 }),
      colored(new THREE.BoxGeometry(0.36, 0.07, 0.08), PALETTE.woodDark, { x: 0.03, y: 1.37, z: 0.6, rx: 0.2, ry: -0.3 }),
      colored(new THREE.BoxGeometry(0.36, 0.07, 0.08), PALETTE.woodDark, { x: 0.37, y: 1.37, z: 0.6, rx: 0.2, ry: 0.3 }),
      colored(new THREE.BoxGeometry(0.1, 0.1, 0.1), STEEL, { x: 0.2, y: 1.37, z: 0.64 }),
      colored(new THREE.BoxGeometry(0.66, 0.025, 0.025), PALETTE.cloth, { x: 0.2, y: 1.39, z: 0.5 }),
      colored(new THREE.BoxGeometry(0.04, 0.04, 0.42), STEEL_DARK, { x: 0.2, y: 1.48, z: 0.5, rx: 0.2 }),
      colored(new THREE.ConeGeometry(0.05, 0.12, 4), STEEL, { x: 0.2, y: 1.41, z: 0.74, rx: Math.PI / 2 + 0.2 })
    ]));

    // Legs: steel greaves and leather boots, pivoting at the hip
    const leg = () => [
      colored(new THREE.BoxGeometry(0.38, 0.62, 0.42), STEEL_DARK, { y: -0.36 }),
      colored(new THREE.BoxGeometry(0.28, 0.2, 0.1), STEEL, { y: -0.46, z: 0.22 }),
      colored(new THREE.BoxGeometry(0.42, 0.34, 0.56), LEATHER, { y: -0.86, z: 0.05 })
    ];
    this.leftLeg = this.limb(leg(), -0.3, 1.05, 0);
    this.rightLeg = this.limb(leg(), 0.3, 1.05, 0);

    // Arms: mail sleeve, steel vambrace, gauntlet
    const arm = () => [
      colored(new THREE.BoxGeometry(0.3, 0.6, 0.32), STEEL_DARK, { y: -0.32 }),
      colored(new THREE.BoxGeometry(0.33, 0.3, 0.36), STEEL, { y: -0.7 }),
      colored(new THREE.BoxGeometry(0.34, 0.22, 0.38), LEATHER, { y: -0.92 })
    ];
    this.leftArm = this.limb(arm(), -0.6, 2.18, 0);
    this.rightArm = this.limb(arm(), 0.6, 2.18, 0);
    this.rightArm.rotation.set(AIM_POSE_X, 0, AIM_POSE_Z);

    // Short cape in team colour, hung from the shoulders so it can sway,
    // with a round team shield slung over it
    this.cape = this.limb([
      colored(new THREE.BoxGeometry(0.9, 0.16, 0.2), teamDark, { y: 0 }),
      colored(flare(new THREE.BoxGeometry(0.88, 1.15, 0.07), 0.15), team, { y: -0.6, z: -0.02 }),
      colored(new THREE.BoxGeometry(0.12, 0.12, 0.06), GOLD, { x: -0.34, y: 0.02, z: 0.1 }),
      colored(new THREE.BoxGeometry(0.12, 0.12, 0.06), GOLD, { x: 0.34, y: 0.02, z: 0.1 }),
      colored(new THREE.CylinderGeometry(0.4, 0.4, 0.05, 8), STEEL, { y: -0.48, z: -0.08, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.35, 0.35, 0.06, 8), team, { y: -0.48, z: -0.1, rx: Math.PI / 2 }),
      colored(new THREE.SphereGeometry(0.1, 6, 4), GOLD, { y: -0.48, z: -0.13 })
    ], 0, 2.3, -0.38);
    this.cape.rotation.x = CAPE_REST;

    // Indicator for local player
    if (this.isLocalPlayer) {
      const indicatorGeometry = new THREE.RingGeometry(1.5, 2, 32);
      const indicatorMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.5
      });
      const indicator = new THREE.Mesh(indicatorGeometry, indicatorMaterial);
      indicator.rotation.x = -Math.PI / 2;
      indicator.position.y = 0.1;
      bodyGroup.add(indicator);
      this.indicator = indicator;
    }

    // Create floating nametag
    this.nametagSprite = this.createNametag();
    this.nametagSprite.position.y = 4.5; // Above the head
    bodyGroup.add(this.nametagSprite);

    // Create health bar (between head and nametag)
    this.healthBar = new HealthBar(this.maxHealth, 2.5, 0.3);
    this.healthBar.getGroup().position.y = 3.8;
    bodyGroup.add(this.healthBar.getGroup());

    this.mesh = bodyGroup;

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.lastMeshPosition.copy(this.mesh.position);

    this.scene.add(this.mesh);
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate rotation
      let rotationDiff = this.targetRotation - this.mesh.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.mesh.rotation.y += rotationDiff * 0.15;

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };

      this.animate();
    }
  }

  // Walk cycle driven by how far the interpolated mesh moved since the last frame
  animate() {
    const now = performance.now() / 1000;
    const dt = this.lastAnimTime === null ? 0 : Math.min(now - this.lastAnimTime, 0.1);
    this.lastAnimTime = now;
    if (dt <= 0) return;

    const pos = this.mesh.position;
    // Clamp so a respawn teleport doesn't register as a sprint
    const moved = Math.min(Math.hypot(pos.x - this.lastMeshPosition.x, pos.z - this.lastMeshPosition.z), 1);
    this.lastMeshPosition.copy(pos);

    const target = Math.min(1, moved / dt / 3);
    this.walk += (target - this.walk) * Math.min(1, dt * 8);
    this.walkPhase += moved * 2.6;

    // Swings are kept modest so limbs stay inside the collision radius
    const swing = Math.sin(this.walkPhase) * 0.45 * this.walk;
    this.leftLeg.rotation.x = swing;
    this.rightLeg.rotation.x = -swing;
    this.leftArm.rotation.x = -swing * 0.65;
    this.rightArm.rotation.x = AIM_POSE_X + Math.sin(this.walkPhase * 2) * 0.03 * this.walk;
    // Rise as the legs spread so the swinging feet stay on the ground
    this.body.position.y = Math.abs(Math.sin(this.walkPhase)) * 0.05 * this.walk;
    this.cape.rotation.x = CAPE_REST + this.walk * 0.14 + Math.sin(now * 2) * 0.02 + Math.sin(this.walkPhase * 2) * 0.04 * this.walk;
  }

  // Set respawning state (dims the player)
  setRespawning(isRespawning) {
    if (!this.mesh || this.isRespawning === isRespawning) return;
    this.isRespawning = isRespawning;

    for (const material of this.materials) {
      material.transparent = isRespawning;
      material.opacity = material.userData.baseOpacity * (isRespawning ? 0.3 : 1);
      material.needsUpdate = true;
    }
  }

  // Aim at a target position
  aimAt(targetX, targetZ) {
    if (this.mesh) {
      const dx = targetX - this.mesh.position.x;
      const dz = targetZ - this.mesh.position.z;
      this.targetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  // Create a floating nametag sprite
  createNametag() {
    const name = this.displayName;

    // Create canvas for the nametag
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    // Set canvas size
    canvas.width = 256;
    canvas.height = 64;

    // Get background color from player color
    const bgColor = typeof this.color === 'string' ? this.color : `#${this.color.toString(16).padStart(6, '0')}`;

    // Draw rounded rectangle background
    const padding = 10;
    const radius = 12;
    context.fillStyle = bgColor;
    context.beginPath();
    context.roundRect(padding, padding, canvas.width - padding * 2, canvas.height - padding * 2, radius);
    context.fill();

    // Add slight border
    context.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    context.lineWidth = 2;
    context.stroke();

    // Draw text
    context.fillStyle = '#ffffff';
    context.font = 'bold 28px Arial, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(name, canvas.width / 2, canvas.height / 2);

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    // Create sprite material
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false, // Always render on top
      depthWrite: false
    });

    // Create sprite
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(4, 1, 1); // Scale to match aspect ratio

    return sprite;
  }

  remove() {
    if (this.nametagSprite) {
      if (this.nametagSprite.material.map) {
        this.nametagSprite.material.map.dispose();
      }
      this.nametagSprite.material.dispose();
    }
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    if (this.indicator) {
      this.indicator.geometry.dispose();
      this.indicator.material.dispose();
    }
    this.geometries.forEach(g => g.dispose());
    this.materials.forEach(m => m.dispose());
    this.geometries = [];
    this.materials = [];
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
  }
}
